import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import { useSelectedSchool } from '@/contexts/SelectedSchoolContext'

export interface SchoolTrackingSheet {
  relationshipId: string
  instructorId: string
  instructorName: string | null
  studentId: string
  studentName: string | null
  itemsCompleted: number
  instructorSignedAt: string | null
  studentSignedAt: string | null
  completionDate: string | null
}

const ITEM_COLUMNS = [
  'item_a_vehicle_components_at', 'item_b_vehicle_control_at', 'item_c_technique_reasons_at',
  'item_d_emotions_decisions_at', 'item_e_predict_anticipate_at', 'item_f_communication_at',
  'item_g_distraction_management_at', 'item_h_observation_skills_at',
] as const

/**
 * Read-only tracking sheets (relationship_progress_sheets) for the school currently
 * selected in the school-admin portal, via "relationship_progress_sheets: school admin
 * read" (migration 069) -- visible only for a relationship that carries this school's own
 * school_id (an employee instructor's student, or a contractor instructor's student this
 * school itself assigned). A private instructor's own students never appear here, by
 * design -- the RLS policy excludes them, this hook does not additionally filter.
 *
 * Mirrors useSchoolStudents.ts's own two-step fetch pattern (relationship rows, then the
 * profiles they reference) since relationship_progress_sheets has no direct FK join
 * available through PostgREST to two different profiles (instructor and student) at once.
 */
export function useSchoolTrackingSheets() {
  const { selectedSchoolId } = useSelectedSchool()
  const [sheets, setSheets] = useState<SchoolTrackingSheet[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchSheets = useCallback(async () => {
    if (!selectedSchoolId) { setSheets([]); setLoading(false); return }
    setLoading(true)
    setError(null)

    try {
      const { data: relationships, error: relErr } = await supabase
        .from('instructor_student_relationships')
        .select('id, instructor_id, student_id')
        .eq('school_id', selectedSchoolId)

      if (relErr) { setError('Could not load relationships.'); return }
      if (!relationships || relationships.length === 0) { setSheets([]); return }

      const relationshipIds = relationships.map(r => r.id)

      // The select string is built dynamically from ITEM_COLUMNS, so supabase-js cannot
      // statically parse it into a row type (it only does that for literal strings) —
      // .returns<any[]>() opts out of that inference rather than fighting it; every field
      // access below is already defensive (optional chaining / the `as any[]` cast at the
      // loop), matching this function's existing style.
      const { data: sheetRows, error: sheetErr } = await supabase
        .from('relationship_progress_sheets')
        .select(`relationship_id, ${ITEM_COLUMNS.join(', ')}, instructor_signed_at, student_signed_at, completion_date`)
        .in('relationship_id', relationshipIds)
        .returns<any[]>()

      if (sheetErr) { setError('Could not load tracking sheets.'); return }
      if (!sheetRows || sheetRows.length === 0) { setSheets([]); return }

      const relById = new Map(relationships.map(r => [r.id, r]))
      const profileIds = [...new Set(
        sheetRows.flatMap(s => {
          const r = relById.get(s.relationship_id as string)
          return r ? [r.instructor_id, r.student_id] : []
        })
      )]

      const { data: profiles, error: profErr } = await supabase
        .from('profiles')
        .select('id, full_name')
        .in('id', profileIds)

      if (profErr) { setError('Could not load names.'); return }
      const nameById = new Map((profiles ?? []).map(p => [p.id, p.full_name]))

      const result: SchoolTrackingSheet[] = []
      for (const s of sheetRows as any[]) {
        const r = relById.get(s.relationship_id)
        if (!r) continue
        const itemsCompleted = ITEM_COLUMNS.filter(col => s[col] != null).length
        result.push({
          relationshipId: s.relationship_id,
          instructorId: r.instructor_id,
          instructorName: nameById.get(r.instructor_id) ?? null,
          studentId: r.student_id,
          studentName: nameById.get(r.student_id) ?? null,
          itemsCompleted,
          instructorSignedAt: s.instructor_signed_at,
          studentSignedAt: s.student_signed_at,
          completionDate: s.completion_date,
        })
      }

      setSheets(result)
    } finally {
      setLoading(false)
    }
  }, [selectedSchoolId])

  useEffect(() => { setSheets([]) }, [selectedSchoolId])
  useEffect(() => { fetchSheets() }, [fetchSheets])

  return { sheets, loading, error, refresh: fetchSheets }
}
